/**
 * SmartBox roster + SCU-association admin endpoints.
 * Platform-only — gated server-side on users.manage + role=platform.
 */
import { apiFetch } from "./apiClient";

export interface SmartboxRosterRow {
  smartbox_id: number;
  smartbox_code: string | null;
  smartbox_name: string | null;
  workcenter_id: number | null;
  work_center: string | null;
  customer_id: number | null;
  customer_name: string | null;
  current_scu_name: string | null;
  tailscale_hostname: string | null;
  hardware_type: string | null;
  last_scan_utc: string | null;
  /** True when the row was created in MTTR (mttr.smartbox_local,
   *  id >= 1,000,000) rather than mirrored from smartbox3 (md_smartbox). */
  is_local: boolean;
  /** Per-smartbox manual enablement (migration 127). Default OFF. */
  manual_checkout_enabled: boolean;
  manual_checkin_enabled: boolean;
}

export interface PendingScuRow {
  scu_name: string;
  last_seen_utc: string | null;
  sources: string | null;
  in_gen5_computer: boolean;
  tailscale_hostname: string | null;
  hardware_type: string | null;
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export function listSmartboxRoster(
  token: string,
): Promise<{ smartboxes: SmartboxRosterRow[] }> {
  return req(token, "/api/admin/smartboxes");
}

export function listPendingScus(
  token: string,
  days = 7,
): Promise<{ lookback_days: number; pending: PendingScuRow[] }> {
  return req(token, `/api/admin/scus/pending?days=${days}`);
}

export function createSmartbox(
  token: string,
  body: {
    code: string;
    name?: string;
    customer_company_id: number;
    workcenter_name?: string;
    note?: string;
  },
): Promise<{
  smartbox_id: number;
  code: string;
  name: string;
  customer_company_id: number;
  workcenter_name: string | null;
  is_local: boolean;
}> {
  return req(token, "/api/admin/smartboxes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function associateScu(
  token: string,
  scu: string,
  smartboxId: number | null,
): Promise<{ scu_name: string; smartbox_id: number | null; changed: boolean }> {
  return req(token, `/api/admin/scus/${encodeURIComponent(scu)}/associate`, {
    method: "POST",
    body: JSON.stringify({ smartbox_id: smartboxId }),
  });
}

/** Toggle per-smartbox manual checkout / checkin (migration 127). */
export function setSmartboxManualConfig(
  token: string,
  smartboxId: number,
  manualCheckoutEnabled: boolean,
  manualCheckinEnabled: boolean,
): Promise<{
  smartbox_id: number;
  manual_checkout_enabled: boolean;
  manual_checkin_enabled: boolean;
}> {
  return req(token, `/api/admin/smartboxes/${smartboxId}/manual-config`, {
    method: "POST",
    body: JSON.stringify({
      manual_checkout_enabled: manualCheckoutEnabled,
      manual_checkin_enabled: manualCheckinEnabled,
    }),
  });
}
